import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useClientVault, useClientsForSelect, useClientVaultTemplates, ClientVaultFolder, ClientVaultRecord } from './useData';
import { createFolder, deleteFolder, createRecord, updateRecord, deleteRecord, uploadFile, deleteFile } from './mutations';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { API_BASE_URL } from '../../config/apiConfig';
import { cn } from '../../lib/utils';
import {
  Button,
  TextInput,
  Textarea,
  Select,
  Label,
  ConfirmationDialog,
} from '../../components/ui';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';

const List: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { colors } = useTheme();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [editingRecord, setEditingRecord] = useState<ClientVaultRecord | null>(null);
  const [folderFormData, setFolderFormData] = useState({ templateId: 0, displayOrder: 0 });
  const [recordFormData, setRecordFormData] = useState<{
    title: string;
    recordType: string;
    data: Record<string, any>;
    tags: string[];
  }>({ title: '', recordType: 'credential', data: {}, tags: [] });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'folder' | 'record' | 'file'; id: number } | null>(null);
  const [showPasswords, setShowPasswords] = useState<Set<number>>(new Set());
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const clientDropdownRef = React.useRef<HTMLDivElement>(null);
  
  const { options, isLoading: clientsLoading } = useClientsForSelect();
  const { vault, isLoading: vaultLoading, mutate } = useClientVault(selectedClientId || 0);
  const { templates, isLoading: templatesLoading } = useClientVaultTemplates();
  const { showNotification } = useNotification();

  // Deep-link: open client from ?clientId=&folderId=
  React.useEffect(() => {
    const clientIdParam = searchParams.get('clientId');
    if (clientIdParam) {
      const cid = parseInt(clientIdParam, 10);
      if (!Number.isNaN(cid)) setSelectedClientId(cid);
    }
  }, [searchParams]);

  // When vault has loaded and URL has folderId, expand that folder
  React.useEffect(() => {
    const folderIdParam = searchParams.get('folderId');
    if (!folderIdParam || !vault?.folders) return;
    const fid = parseInt(folderIdParam, 10);
    if (Number.isNaN(fid)) return;
    const hasFolder = vault.folders.some((f) => f.id === fid);
    if (hasFolder) setExpandedFolders((prev) => new Set(prev).add(fid));
  }, [searchParams, vault?.folders]);

  // Filter clients based on search term
  const filteredClientOptions = React.useMemo(() => {
    if (!clientSearchTerm.trim()) return options;
    const searchLower = clientSearchTerm.toLowerCase();
    return options.filter(option => 
      option.label.toLowerCase().includes(searchLower)
    );
  }, [options, clientSearchTerm]);

  // Get selected client label
  const selectedClientLabel = React.useMemo(() => {
    if (!selectedClientId) return '';
    const option = options.find(opt => Number(opt.value) === selectedClientId);
    return option?.label || '';
  }, [selectedClientId, options]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setIsClientDropdownOpen(false);
      }
    };

    if (isClientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isClientDropdownOpen]);

  // Get available templates (exclude ones already used by this client)
  const availableTemplates = React.useMemo(() => {
    if (!vault || !templates) return templates || [];
    const usedTemplateIds = new Set(vault.folders.map(f => f.templateId));
    return templates.filter(t => !usedTemplateIds.has(t.id));
  }, [vault, templates]);

  const toggleFolder = (folderId: number) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleCreateFolder = async () => {
    if (!selectedClientId || !folderFormData.templateId) {
      showNotification('Please select a folder template', 'error');
      return;
    }

    try {
      await createFolder(selectedClientId, { templateId: folderFormData.templateId });
      showNotification('Folder created successfully', 'success');
      setShowFolderModal(false);
      setFolderFormData({ templateId: 0, displayOrder: 0 });
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to create folder', 'error');
    }
  };

  const handleDeleteFolder = async (folderId: number) => {
    if (!selectedClientId) return;

    try {
      await deleteFolder(selectedClientId, folderId);
      showNotification('Folder deleted successfully', 'success');
      setDeleteConfirm(null);
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to delete folder', 'error');
    }
  };

  const handleCreateRecord = async () => {
    if (!selectedClientId || !selectedFolderId || !recordFormData.title) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    try {
      await createRecord(selectedClientId, selectedFolderId, recordFormData);
      showNotification('Record created successfully', 'success');
      setShowRecordModal(false);
      setRecordFormData({ title: '', recordType: 'credential', data: {}, tags: [] });
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to create record', 'error');
    }
  };

  const handleUpdateRecord = async () => {
    if (!selectedClientId || !selectedFolderId || !editingRecord || !recordFormData.title) {
      return;
    }

    try {
      await updateRecord(selectedClientId, selectedFolderId, editingRecord.id, recordFormData);
      showNotification('Record updated successfully', 'success');
      setShowRecordModal(false);
      setEditingRecord(null);
      setRecordFormData({ title: '', recordType: 'credential', data: {}, tags: [] });
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to update record', 'error');
    }
  };

  const handleDeleteRecord = async (recordId: number) => {
    if (!selectedClientId || !selectedFolderId) return;

    try {
      await deleteRecord(selectedClientId, selectedFolderId, recordId);
      showNotification('Record deleted successfully', 'success');
      setDeleteConfirm(null);
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to delete record', 'error');
    }
  };

  const handleUploadFile = async () => {
    if (!selectedClientId || !selectedFolderId || !selectedFile) {
      showNotification('Please select a file', 'error');
      return;
    }

    try {
      await uploadFile(selectedClientId, selectedFolderId, selectedFile);
      showNotification('File uploaded successfully', 'success');
      setShowFileModal(false);
      setSelectedFile(null);
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to upload file', 'error');
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    if (!selectedClientId || !selectedFolderId) return;

    try {
      await deleteFile(selectedClientId, selectedFolderId, fileId);
      showNotification('File deleted successfully', 'success');
      setDeleteConfirm(null);
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to delete file', 'error');
    }
  };

  const handleDownloadFile = async (fileId: number, folderId: number, fileName: string) => {
    if (!selectedClientId) return;

    try {
      const token = localStorage.getItem('access_token');
      const url = `${API_BASE_URL}/client-vault/${selectedClientId}/files/${fileId}?folderId=${folderId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Get filename from Content-Disposition header or use the file name
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = fileName;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // Decode URI if needed
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // If decoding fails, use as is
          }
        }
      }

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      showNotification('File downloaded successfully', 'success');
    } catch (error: any) {
      console.error('Download error:', error);
      showNotification(error.message || 'Failed to download file', 'error');
    }
  };

  const openFolderModal = () => {
    setFolderFormData({ templateId: 0, displayOrder: 0 });
    setShowFolderModal(true);
  };

  const openRecordModal = (record?: ClientVaultRecord) => {
    if (record) {
      setEditingRecord(record);
      setRecordFormData({
        title: record.title,
        recordType: record.recordType,
        data: record.data,
        tags: record.tags || [],
      });
    } else {
      setEditingRecord(null);
      setRecordFormData({ title: '', recordType: 'credential', data: {}, tags: [] });
    }
    setShowRecordModal(true);
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const iconMap: Record<string, string> = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      txt: '📃',
      png: '🖼️',
      jpg: '🖼️',
      jpeg: '🖼️',
      gif: '🖼️',
    };
    return iconMap[ext || ''] || '📎';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (clientsLoading) {
    return (
      <div className={cn('flex flex-col items-center justify-center min-h-[50vh]', colors.background)}>
        <div className={cn('h-10 w-10 animate-spin rounded-full border-2 border-t-transparent', colors.primary)} />
        <p className={cn('mt-4 text-sm', colors.textMuted)}>Loading clients...</p>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen', colors.background)}>
      {/* Header */}
      <header className={cn('sticky top-0 z-10 border-b', colors.surface, colors.border)}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="flex items-center gap-2 shrink-0">
                <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', colors.info)}>
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <span className={cn('text-lg font-semibold truncate', colors.text)}>Client Vault</span>
              </div>
              <div className="w-full max-w-md relative" ref={clientDropdownRef}>
                <TextInput
                  type="text"
                  value={isClientDropdownOpen ? clientSearchTerm : selectedClientLabel}
                  onChange={(e) => {
                    setClientSearchTerm(e.target.value);
                    setIsClientDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setIsClientDropdownOpen(true);
                    setClientSearchTerm('');
                  }}
                  placeholder="Select client..."
                  className="w-full pr-10 rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (selectedClientId) {
                      setSelectedClientId(null);
                      setSelectedFolderId(null);
                      setExpandedFolders(new Set());
                    } else {
                      setIsClientDropdownOpen(!isClientDropdownOpen);
                    }
                  }}
                  className={cn('absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md', colors.textMuted, 'hover:bg-gray-100 dark:hover:bg-gray-700')}
                  aria-label={selectedClientId ? 'Clear client' : 'Toggle list'}
                >
                  {selectedClientId ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className={`w-4 h-4 transition-transform ${isClientDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
                {isClientDropdownOpen && (
                  <div className={cn('absolute z-50 mt-1 w-full rounded-lg border shadow-lg max-h-56 overflow-y-auto', colors.surface, colors.border)}>
                    {filteredClientOptions.length === 0 ? (
                      <div className={cn('px-4 py-3 text-sm', colors.textMuted)}>No clients found</div>
                    ) : (
                      filteredClientOptions.map((option) => {
                        const optionValue = typeof option.value === 'string' ? Number(option.value) : option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setSelectedClientId(optionValue);
                              setSelectedFolderId(null);
                              setExpandedFolders(new Set());
                              setIsClientDropdownOpen(false);
                              setClientSearchTerm('');
                            }}
                            className={cn(
                              'w-full px-4 py-2.5 text-left text-sm transition-colors',
                              selectedClientId === optionValue ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200' : colors.text,
                              'hover:bg-gray-50 dark:hover:bg-gray-800'
                            )}
                          >
                            {option.label}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/vault-templates"
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                  colors.border,
                  colors.surfaceHover,
                  'hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
                title="Vault Templates"
                aria-label="Vault Templates"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
                </svg>
              </Link>
              {!vaultLoading && vault && vault.folders.length > 0 && (
                <Button
                  onClick={() => openFolderModal()}
                  variant="default"
                  size="default"
                  className="h-9 gap-1.5"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New folder
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        {!selectedClientId && (
          <div className={cn('flex flex-col items-center justify-center py-24 text-center')}>
            <div className={cn('flex h-16 w-16 items-center justify-center rounded-2xl', colors.surfaceHover)}>
              <svg className={cn('h-8 w-8', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h2 className={cn('mt-4 text-lg font-semibold', colors.text)}>Select a client</h2>
            <p className={cn('mt-1 text-sm max-w-sm', colors.textMuted)}>Choose a client above to view and manage their vault (credentials, notes, files).</p>
          </div>
        )}
        {vaultLoading && selectedClientId && (
          <div className="flex flex-col items-center justify-center py-24">
            <div className={cn('h-10 w-10 animate-spin rounded-full border-2 border-t-transparent', colors.primary)} />
            <p className={cn('mt-4 text-sm', colors.textMuted)}>Loading vault...</p>
          </div>
        )}

        {!vaultLoading && vault && vault.folders.length === 0 && selectedClientId && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className={cn('flex h-20 w-20 items-center justify-center rounded-2xl', colors.info)}>
              <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <h2 className={cn('mt-6 text-xl font-semibold', colors.text)}>No folders yet</h2>
            <p className={cn('mt-2 max-w-sm text-sm', colors.textMuted)}>Create a folder to store credentials, notes, and files for this client.</p>
            <Button onClick={() => openFolderModal()} variant="default" className="mt-6 gap-2">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create first folder
            </Button>
          </div>
        )}

        {!vaultLoading && vault && vault.folders.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {vault.folders.map((folder) => {
              const isExpanded = expandedFolders.has(folder.id);
              const folderColor = folder.template.color || '#6366f1';
              return (
                <div
                  key={folder.id}
                  className={cn(
                    'rounded-xl border overflow-hidden transition-shadow',
                    colors.surface,
                    colors.border,
                    'hover:shadow-md'
                  )}
                >
                  <div
                    className={cn('flex border-l-4', colors.surface)}
                    style={{ borderLeftColor: folderColor }}
                  >
                    <div
                      className="flex-1 min-w-0 p-4 cursor-pointer"
                      onClick={() => toggleFolder(folder.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-lg"
                          style={{ backgroundColor: `${folderColor}20`, color: folderColor }}
                        >
                          {folder.template.icon || '📁'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className={cn('font-semibold truncate', colors.text)}>{folder.template.name}</h3>
                          {folder.template.description && (
                            <p className={cn('text-xs truncate mt-0.5', colors.textMuted)}>{folder.template.description}</p>
                          )}
                        </div>
                        <div className={cn('flex items-center gap-2 text-xs', colors.textMuted)}>
                          <span>{folder.records.length} records</span>
                          <span>·</span>
                          <span>{folder.files.length} files</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleFolder(folder.id); }}
                          className={cn('p-1.5 rounded-md shrink-0', 'hover:bg-gray-100 dark:hover:bg-gray-700', colors.textMuted)}
                          aria-expanded={isExpanded}
                        >
                          <svg className={cn('w-5 h-5 transition-transform', isExpanded && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className={cn('flex gap-1 px-4 py-2 border-t', colors.border)}>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFolderId(folder.id);
                        openRecordModal();
                      }}
                      variant="ghost"
                      size="sm"
                      className={cn('flex-1 font-medium', colors.primaryFg, colors.interactiveHover)}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Record
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFolderId(folder.id);
                        setShowFileModal(true);
                      }}
                      variant="ghost"
                      size="sm"
                      className={cn('flex-1 font-medium', colors.primaryFg, colors.interactiveHover)}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({ type: 'folder', id: folder.id });
                      }}
                      variant="ghost"
                      size="icon"
                      className={cn(colors.error)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>

                  <div
                    className={cn('overflow-hidden transition-all duration-300 ease-out', isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0')}
                  >
                    <div className={cn('border-t', colors.border, colors.background)}>
                      <div className="p-4">
                        <h4 className={cn('text-xs font-semibold uppercase tracking-wide mb-3', colors.textMuted)}>
                          Records · {folder.records.length}
                        </h4>
                        {folder.records.length === 0 ? (
                          <div className={cn('rounded-lg border border-dashed py-6 text-center', colors.border, colors.textMuted)}>
                            <p className="text-sm">No records yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {folder.records.map((record) => (
                              <div
                                key={record.id}
                                className={cn('rounded-lg border p-3', colors.surface, colors.border)}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className={cn('font-bold', colors.text)}>{record.title}</span>
                                      <span className={cn('px-2 py-0.5 text-xs font-semibold rounded-full', colors.surfaceHover, colors.textSecondary)}>
                                        {record.recordType}
                                      </span>
                                    </div>
                                    
                                    {record.recordType === 'credential' && record.data && (
                                      <div className={cn('mt-2 p-3 rounded-lg space-y-2', colors.surfaceHover, colors.border, 'border')}>
                                        {(record.data as any).username && (
                                          <div className="flex items-center gap-3 group/item">
                                            <div className={cn('flex-shrink-0 w-20 text-xs font-bold uppercase tracking-wide', colors.textSecondary)}>
                                              Username
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className={cn('flex items-center gap-2 rounded-lg px-3 py-2 border', colors.surface, 'bg-opacity-80', colors.border)}>
                                                <span className={cn('text-sm font-mono flex-1 truncate', colors.text)}>
                                                  {(record.data as any).username}
                                                </span>
                                                <button
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                      const text = (record.data as any).username;
                                                      if (!text) return;
                                                      
                                                      if (navigator.clipboard && navigator.clipboard.writeText) {
                                                        await navigator.clipboard.writeText(text);
                                                        showNotification('Username copied!', 'success');
                                                      } else {
                                                        const textArea = document.createElement('textarea');
                                                        textArea.value = text;
                                                        textArea.style.position = 'fixed';
                                                        textArea.style.left = '-999999px';
                                                        document.body.appendChild(textArea);
                                                        textArea.select();
                                                        document.execCommand('copy');
                                                        document.body.removeChild(textArea);
                                                        showNotification('Username copied!', 'success');
                                                      }
                                                    } catch (error) {
                                                      showNotification('Failed to copy', 'error');
                                                    }
                                                  }}
                                                  className={cn('p-1.5 rounded-lg transition-all hover:scale-110', colors.primaryFg, colors.interactiveHover)}
                                                  title="Copy username"
                                                >
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                  </svg>
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {(record.data as any).password && (
                                          <div className="flex items-center gap-3 group/item">
                                            <div className={cn('flex-shrink-0 w-20 text-xs font-bold uppercase tracking-wide', colors.textSecondary)}>
                                              Password
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className={cn('flex items-center gap-2 rounded-lg px-3 py-2 border', colors.surface, 'bg-opacity-80', colors.border)}>
                                                <span className={cn('text-sm font-mono flex-1', colors.text)}>
                                                  {showPasswords.has(record.id) 
                                                    ? (record.data as any).password 
                                                    : '••••••••••••'}
                                                </span>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const newSet = new Set(showPasswords);
                                                    if (newSet.has(record.id)) {
                                                      newSet.delete(record.id);
                                                    } else {
                                                      newSet.add(record.id);
                                                    }
                                                    setShowPasswords(newSet);
                                                  }}
                                                  className={cn('p-1.5 rounded-lg transition-all hover:scale-110', colors.primaryFg, colors.interactiveHover)}
                                                  title={showPasswords.has(record.id) ? "Hide password" : "Show password"}
                                                >
                                                  {showPasswords.has(record.id) ? (
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                  ) : (
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                  )}
                                                </button>
                                                <button
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                      const text = (record.data as any).password;
                                                      if (!text) return;
                                                      
                                                      if (navigator.clipboard && navigator.clipboard.writeText) {
                                                        await navigator.clipboard.writeText(text);
                                                        showNotification('Password copied!', 'success');
                                                      } else {
                                                        const textArea = document.createElement('textarea');
                                                        textArea.value = text;
                                                        textArea.style.position = 'fixed';
                                                        textArea.style.left = '-999999px';
                                                        document.body.appendChild(textArea);
                                                        textArea.select();
                                                        document.execCommand('copy');
                                                        document.body.removeChild(textArea);
                                                        showNotification('Password copied!', 'success');
                                                      }
                                                    } catch (error) {
                                                      showNotification('Failed to copy', 'error');
                                                    }
                                                  }}
                                                  className={cn('p-1.5 rounded-lg transition-all hover:scale-110', colors.primaryFg, colors.interactiveHover)}
                                                  title="Copy password"
                                                >
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                  </svg>
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        {(record.data as any).url && (
                                          <div className="flex items-center gap-3">
                                            <div className={cn('flex-shrink-0 w-20 text-xs font-bold uppercase tracking-wide', colors.textSecondary)}>
                                              URL
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <a
                                                href={(record.data as any).url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={cn('text-sm hover:underline truncate block rounded-lg px-3 py-2 border', colors.primaryFg, colors.surface, 'bg-opacity-80', colors.border)}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                {(record.data as any).url}
                                              </a>
                                            </div>
                                          </div>
                                        )}
                                        {(record.data as any).notes && (
                                          <div className="flex items-start gap-3">
                                            <div className={cn('flex-shrink-0 w-20 text-xs font-bold uppercase tracking-wide pt-2', colors.textSecondary)}>
                                              Notes
                                            </div>
                                            <div className="flex-1">
                                              <p className={cn('text-sm rounded-lg px-3 py-2 border', colors.textSecondary, colors.surface, 'bg-opacity-80', colors.border)}>
                                                {(record.data as any).notes}
                                              </p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Note Content */}
                                    {record.recordType === 'note' && record.data && (record.data as any).content && (
                                      <div className={cn('mt-3 p-3 rounded-xl text-sm border', colors.surfaceHover, colors.textSecondary, colors.border)}>
                                        {(record.data as any).content}
                                      </div>
                                    )}
                                    
                                    {record.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 mt-3">
                                        {record.tags.map((tag, idx) => (
                                          <span
                                            key={idx}
                                            className={cn('px-2.5 py-1 text-xs font-semibold rounded-full border', colors.info, colors.primaryBorder)}
                                          >
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-1 ml-3 flex-shrink-0">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFolderId(folder.id);
                                        openRecordModal(record);
                                      }}
                                      className={cn('p-2 rounded-lg transition-all hover:scale-110', colors.primaryFg, colors.interactiveHover)}
                                      title="Edit"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFolderId(folder.id);
                                        setDeleteConfirm({ type: 'record', id: record.id });
                                      }}
                                      className={cn('p-2 rounded-lg transition-all hover:scale-110', colors.error)}
                                      title="Delete"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className={cn('p-4 border-t', colors.border)}>
                        <h4 className={cn('text-xs font-semibold uppercase tracking-wide mb-3', colors.textMuted)}>
                          Files · {folder.files.length}
                        </h4>
                        {folder.files.length === 0 ? (
                          <div className={cn('rounded-lg border border-dashed py-6 text-center', colors.border, colors.textMuted)}>
                            <p className="text-sm">No files yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {folder.files.map((file) => (
                              <div
                                key={file.id}
                                className={cn('rounded-lg border p-3', colors.surface, colors.border)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className={cn('w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0', colors.info)}>
                                      {getFileIcon(file.fileName)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={cn('font-semibold truncate', colors.text)}>{file.fileName}</p>
                                      <p className={cn('text-xs', colors.textMuted)}>{formatFileSize(file.fileSize)}</p>
                                    </div>
                                  </div>
                                  <div className="flex gap-1 ml-3 flex-shrink-0">
                                    <button
                                      onClick={() => handleDownloadFile(file.id, folder.id, file.fileName)}
                                      className={cn('p-2 rounded-lg transition-all hover:scale-110', colors.success)}
                                      title="Download"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFolderId(folder.id);
                                        setDeleteConfirm({ type: 'file', id: file.id });
                                      }}
                                      className={cn('p-2 rounded-lg transition-all hover:scale-110', colors.error)}
                                      title="Delete"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Folder Modal */}
      <Modal isOpen={showFolderModal} onClose={() => setShowFolderModal(false)} size="md">
        <ModalHeader>
          <ModalTitle>Create Folder</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
              <div>
                <Label htmlFor="folder-template" required>Folder Template</Label>
                {templatesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className={cn('animate-spin rounded-full h-6 w-6 border-b-2', colors.primary)}></div>
                  </div>
                ) : availableTemplates.length === 0 ? (
                  <div className={cn('p-4 rounded-lg border', colors.warning)}>
                    <p className={cn('text-sm', colors.text)}>All available folder templates have been used for this client.</p>
                  </div>
                ) : (
                  <Select
                    id="folder-template"
                    name="folder-template"
                    value={folderFormData.templateId > 0 ? folderFormData.templateId.toString() : ''}
                    onChange={(e) => setFolderFormData({ ...folderFormData, templateId: e.target.value ? Number(e.target.value) : 0 })}
                    placeholder="-- Select a folder template --"
                    showClearButton={true}
                    options={availableTemplates.map((template) => ({
                      value: template.id.toString(),
                      label: [template.icon || '📁', template.name, template.description ? `- ${template.description}` : ''].filter(Boolean).join(' '),
                    }))}
                  />
                )}
                {folderFormData.templateId > 0 && (() => {
                  const selectedTemplate = templates?.find(t => t.id === folderFormData.templateId);
                  return selectedTemplate ? (
                    <div className={cn('mt-4 p-4 rounded-xl border', colors.surfaceHover, colors.border)}>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-lg"
                          style={{ backgroundColor: selectedTemplate.color || '#3B82F6' }}
                        >
                          {selectedTemplate.icon || '📁'}
                        </div>
                        <div>
                          <p className={cn('font-bold', colors.text)}>{selectedTemplate.name}</p>
                          {selectedTemplate.description && (
                            <p className={cn('text-sm mt-1', colors.textSecondary)}>{selectedTemplate.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button
            onClick={() => {
              setShowFolderModal(false);
              setFolderFormData({ templateId: 0, displayOrder: 0 });
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="default"
            disabled={!folderFormData.templateId || templatesLoading || availableTemplates.length === 0}
          >
            Create
          </Button>
        </ModalFooter>
      </Modal>

      {/* Record Modal */}
      <Modal isOpen={showRecordModal && !!selectedFolderId} onClose={() => setShowRecordModal(false)} size="lg">
        <ModalHeader>
          <ModalTitle>{editingRecord ? 'Edit Record' : 'Create Record'}</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="record-title" required>Title</Label>
              <TextInput
                id="record-title"
                value={recordFormData.title}
                onChange={(e) => setRecordFormData({ ...recordFormData, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="record-type">Record Type</Label>
              <Select
                id="record-type"
                name="record-type"
                value={recordFormData.recordType}
                onChange={(e) => setRecordFormData({ ...recordFormData, recordType: e.target.value, data: {} })}
                showClearButton={false}
                options={[
                  { value: 'credential', label: 'Credential' },
                  { value: 'note', label: 'Note' },
                  { value: 'contact', label: 'Contact' },
                  { value: 'custom', label: 'Custom' },
                ]}
              />
            </div>
            {recordFormData.recordType === 'credential' && (
              <div className={cn('space-y-4 p-4 rounded-xl border', colors.surfaceHover, colors.border)}>
                <div>
                  <Label htmlFor="credential-username">Username</Label>
                  <TextInput
                    id="credential-username"
                    type="text"
                    value={(recordFormData.data as any).username || ''}
                    onChange={(e) => setRecordFormData({
                      ...recordFormData,
                      data: { ...recordFormData.data, username: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="credential-password">Password</Label>
                  <TextInput
                    id="credential-password"
                    type="password"
                    value={(recordFormData.data as any).password || ''}
                    onChange={(e) => setRecordFormData({
                      ...recordFormData,
                      data: { ...recordFormData.data, password: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="credential-url">URL</Label>
                  <TextInput
                    id="credential-url"
                    type="text"
                    value={(recordFormData.data as any).url || ''}
                    onChange={(e) => setRecordFormData({
                      ...recordFormData,
                      data: { ...recordFormData.data, url: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="credential-notes">Notes</Label>
                  <Textarea
                    id="credential-notes"
                    value={(recordFormData.data as any).notes || ''}
                    onChange={(e) => setRecordFormData({
                      ...recordFormData,
                      data: { ...recordFormData.data, notes: e.target.value }
                    })}
                    rows={3}
                  />
                </div>
              </div>
            )}
            {recordFormData.recordType === 'note' && (
              <div>
                <Label htmlFor="note-content">Content</Label>
                <Textarea
                  id="note-content"
                  value={(recordFormData.data as any).content || ''}
                  onChange={(e) => setRecordFormData({
                    ...recordFormData,
                    data: { ...recordFormData.data, content: e.target.value }
                  })}
                  rows={5}
                />
              </div>
            )}
            <div>
              <Label htmlFor="record-tags">Tags (comma-separated)</Label>
              <TextInput
                id="record-tags"
                value={recordFormData.tags.join(', ')}
                onChange={(e) => setRecordFormData({
                  ...recordFormData,
                  tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                })}
                placeholder="tag1, tag2, tag3"
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button
            onClick={() => {
              setShowRecordModal(false);
              setEditingRecord(null);
              setRecordFormData({ title: '', recordType: 'credential', data: {}, tags: [] });
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={editingRecord ? handleUpdateRecord : handleCreateRecord}
            variant="default"
          >
            {editingRecord ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* File Upload Modal */}
      <Modal isOpen={showFileModal && !!selectedFolderId} onClose={() => setShowFileModal(false)} size="md">
        <ModalHeader>
          <ModalTitle>Upload File</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="file-input" required>Select File</Label>
              <input
                id="file-input"
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className={cn('mt-1 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold', colors.textMuted, colors.info)}
              />
              {selectedFile && (
                <p className={cn('mt-2 text-sm', colors.textSecondary)}>
                  Selected: <span className="font-medium">{selectedFile.name}</span> ({(selectedFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button
            onClick={() => {
              setShowFileModal(false);
              setSelectedFile(null);
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadFile}
            variant="default"
            disabled={!selectedFile}
          >
            Upload
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            if (deleteConfirm.type === 'folder') {
              handleDeleteFolder(deleteConfirm.id);
            } else if (deleteConfirm.type === 'record') {
              handleDeleteRecord(deleteConfirm.id);
            } else if (deleteConfirm.type === 'file') {
              handleDeleteFile(deleteConfirm.id);
            }
          }
        }}
        title={`Delete ${deleteConfirm?.type || 'item'}`}
        message={`Are you sure you want to delete this ${deleteConfirm?.type || 'item'}? This action cannot be undone.`}
      />
    </div>
  );
};

export default List;
